import { CategorySummary, PackageratorSelectOption, PricebookEntry, Quote } from '@nuso/quoting/types';
import { identity, sortBy } from 'lodash';
import { SubProductNames } from './packagerator-helpers';
import { contractMonthDiff } from './billing-helpers';

export function pricebookEntrySorter() {
  return [
    (entry: PricebookEntry) => entry.categoryName !== 'SIP Trunks', // False comes first, we want Recurring first
    (entry: PricebookEntry) => entry.mrrType !== 'Recurring', // False comes first, we want Recurring first
    (entry: PricebookEntry) => entry.description.indexOf('Bundle') === -1, // False comes first, we want bundles first
    (entry: PricebookEntry) => entry.description.indexOf('Additional') !== -1, // Would rather look at subType Add On but want last
    (entry: PricebookEntry) => entry.callPathCount,
    (entry: PricebookEntry) => entry.minutesCount,
    (entry: PricebookEntry) => faxPageCount(entry),
    (entry: PricebookEntry) => faxStandardBeforeHipaa(entry),
    (entry: PricebookEntry) => simplyConnectOrder(entry),
    (entry: PricebookEntry) => simplyOrder(entry),
    (entry: PricebookEntry) => {
      const m = entry.description.match(/Metered (\d\.\d+)/);
      if (m) {
        return +m[1]; // sort by
      }

      return 99999;
    },
    (entry: PricebookEntry) => !entry.disableQuantityEdit,
    (entry: PricebookEntry) => entry.name,
  ];
}


// With adding simply connect, if they chose "Simply Connect" for the quote type, we want to display
// the sidebar in a different order (We'll put it on top, maybe along with IP-Fax) Note: there are some
// other places that we hard code this order, and will need to update if changes ()
export function sortCloudApplicationCategorySummaries(visible: CategorySummary[]): CategorySummary[] {
  return sortBy(visible, [(cs) => {
    if (cs.key === 'simplyconnect') {
      return 0;
    }
    if (cs.key === 'ipfax') {
      return 1;
    }

    return 2;
  }]);
}

export function calculatePricebookProductRateAndCost(entry: PricebookEntry, contractStart: Date, contractEnd: Date) {
  const months = contractMonthDiff(contractStart, contractEnd);

  if (months >= 60) {
    return {
      cost: entry.pp60,
      rate: entry.rp60
    }
  }
  else if (months >= 36) {
    return {
      cost: entry.pp36,
      rate: entry.rp36
    }
  }
  else if (months >= 24) {
    return {
      cost: entry.pp24,
      rate: entry.rp24
    }
  }
  else if (months >= 12) {
    return {
      cost: entry.pp12,
      rate: entry.rp12
    }
  }

  return {
    cost: entry.partnerPrice,
    rate: entry.retailPrice
  }
}


export function usageTypeSorter() {
  return [
    (type: string) => !type.startsWith('Unlimited'),
    identity
  ]
}

export function mainServiceOptions(pricebookEntries: PricebookEntry[], quoteServiceTypes: string[]): PackageratorSelectOption[] {
  const mainServices: string[] = pricebookEntries
    .filter(pe => pe.active && pe.mainServices)
    .map(pe => pe.mainServices)
    .reduce((all, services) => all.concat(services), [])
    .filter((service) => {
      if (quoteServiceTypes.includes('UCaaS') && service == 'UCaaS Green') {
        return false;
      }
      if (quoteServiceTypes.includes('UCaaS Green') && service == 'UCaaS') {
        return false;
      }
      return true;
    });

  const uniqServices = [...new Set(mainServices)];

  return uniqServices.map((service) => {
    return { label: service, value: service }
  })
}


export function pricebookCategoriesForService(
  pricebookEntries: PricebookEntry[],
  service: string | null,
): PackageratorSelectOption[] {
  if (!service) {
    return [];
  }

  const servicesByCategory = pricebookEntries
    .filter(pe => pe.active && pe.productCategory && pe.productCategory !== 'Do Not Display')
    .reduce((cats: { name: string; services: string[] }[], pe) => {
      let existing = cats.find(item => item.name === pe.productCategory);
      if (!existing) {
        existing = {
          name: pe.productCategory,
          services: []
        }
        cats.push(existing);
      }

      existing.services = [...new Set([...existing.services, ...pe.mainServices])];
      return cats;
    }, [])


  const categoriesForService = servicesByCategory
    .filter((cat) => cat.services.includes(service))
    .map(cat => cat.name);

  return categoriesForService
    .sort()
    .reverse()
    .map(value => ({ label: value, value }));
}

export function pricebookProductsForCategory(
  quote: Quote,
  pricebookEntries: PricebookEntry[],
  service: string | null | undefined,
  category: string | null,
): PackageratorSelectOption[] {
  if (!service || !category) {
    return [];
  }

  return pricebookEntries
    .filter((entry: PricebookEntry) => {
      return entry.active
        && entry.mainServices.includes(service)
        && entry.productCategory === category
        && (entry as unknown as Record<string, unknown>)['rp'+quote.termMonths] != null
        && (entry as unknown as Record<string, unknown>)['pp'+quote.termMonths] != null;
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((product: PricebookEntry) => ({ label: product.name, value: product.id }));
}

export function pricebookHardwareForProduct(
  quote: Quote,
  pricebookEntries: PricebookEntry[],
  service: string | null | undefined,
  category: string | null | undefined,
  productId: string | null,
): PackageratorSelectOption[] {
  return ucaasSubproductsForCategory(quote, pricebookEntries, service, category, productId, SubProductNames.hardware);
}

export function pricebookAddonsForProduct(
  quote: Quote,
  pricebookEntries: PricebookEntry[],
  service: string | null | undefined,
  category: string | null | undefined,
  productId: string | null,
): PackageratorSelectOption[] {
  return ucaasSubproductsForCategory(quote, pricebookEntries, service, category, productId, SubProductNames.addon);
}

function ucaasSubproductsForCategory(
  quote: Quote,
  pricebookEntries: PricebookEntry[],
  service: string | null | undefined,
  currentCategory: string | null | undefined,
  productId: string | null,
  subProduct: string,
): PackageratorSelectOption[] {
  if (!service || !productId || currentCategory !== 'UCaaS User Packages') {
    return [];
  }

  // Non-null assertion preserves existing behavior: if no matching entry is found, the
  // original code threw a TypeError on product.name below -- this file was never
  // type-checked before this batch, so that isn't something to change here.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see comment above
  const product = pricebookEntries.find((entry: PricebookEntry) => entry.id === productId)!;

  return pricebookEntries
    .filter((entry: PricebookEntry) => {
      return entry.active
        && entry.mainServices.includes(service)
        && entry.availableOnPackages.includes(product.name)
        && entry.subProduct === subProduct
        && (entry as unknown as Record<string, unknown>)['rp'+quote.termMonths] != null
        && (entry as unknown as Record<string, unknown>)['pp'+quote.termMonths] != null;
  })
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(h => ({ label: h.name, value: h.id }));
}

function faxPageCount(entry: PricebookEntry) {
  if (entry.categoryKey !== 'ipfax') {
    return 0;
  }

  const countMatch = entry.description.match(/-\s+(\d+)/);
  if (!countMatch) {
    return 0;
  }

  return +countMatch[1];
}

function faxStandardBeforeHipaa(entry: PricebookEntry) {
  // we want standard, then unlimited pages, then hipaa
  let score = 0;
  if (entry.categoryKey !== 'ipfax') {
    return 0;
  }

  if (entry.description.indexOf('Unlimited Users') !== -1) {
    score += 1;
  }

  if (entry.description.indexOf('HIPAA') !== -1) {
    score += 2;
  }

  return score;
}

function simplyConnectOrder(entry: PricebookEntry) {
  // Ordering SimplyConnect by Single User, Package, Add User, DID, and Messages
  if (entry.categoryKey !== 'simplyconnect') {
    return 0;
  }

  let score = 0;

  if (entry.description.indexOf('Single') === -1) {
    score += 1;
  }
  if (entry.description.indexOf('Add') !== -1) {
    score += 2;
  }
  if (entry.description.indexOf('DID') !== -1) {
    score += 3;
  }
  if (entry.description.indexOf('Messages') !== -1) {
    score += 4;
  }

  return score;
}

function simplyOrder(entry: PricebookEntry) {
  // Ensuring all numbered lists are ordered by size
  if (entry.categoryKey !== 'simplyconnect') {
    return 0;
  }

  const simply = entry.description.match(/(\d+)/);
  if (!simply) {
    return 0;
  }

  return +simply[1];
}
